/**
 * «Έχεις αλλαγές που δεν αποθηκεύτηκαν» της σελίδας «Κατάλογος»: ΜΙΑ μπάρα για όλη τη σελίδα, με
 * «Τι άλλαξε;» (παλιά → νέα τιμή, αναίρεση ανά γραμμή). Κολλημένη κάτω, στο πλάτος της στήλης της
 * σελίδας (όπως το settings/UnsavedChangesBar), ώστε να μην καλύπτει το πλαϊνό μενού.
 */
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, Loader2, Undo2 } from 'lucide-react';
import type { ChangeEntry } from './catalogChanges';

interface CatalogSaveBarProps {
  changes: ChangeEntry[];
  owners: { products: number; categories: number };
  saving: boolean;
  open: boolean;
  disabled?: boolean;
  onToggle: () => void;
  onUndoAll: () => void;
  onUndo: (entry: ChangeEntry) => void;
  onSave: () => void;
}

const MD = '(min-width: 768px)';

const CatalogSaveBar: React.FC<CatalogSaveBarProps> = ({ changes, owners, saving, open, disabled, onToggle, onUndoAll, onUndo, onSave }) => {
  const { t } = useTranslation(['catalog']);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ left: number; width: number } | null>(null);
  const dirty = changes.length > 0;

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!dirty || !anchor) return undefined;
    const measure = () => {
      const wide = typeof window.matchMedia === 'function' && window.matchMedia(MD).matches;
      const rect = anchor.getBoundingClientRect();
      // Με zoom στη ρίζα (public/index.html) το getBoundingClientRect δίνει px οθόνης, ενώ τα left/width
      // του style είναι CSS px που μικραίνουν ξανά: η μπάρα έβγαινε αριστερότερα και στενότερη από τη
      // στήλη. offsetWidth = CSS px, ο λόγος τους είναι το zoom — όπως στο settings/UnsavedChangesBar.
      const k = anchor.offsetWidth > 0 ? rect.width / anchor.offsetWidth : 1;
      setBox(wide && rect.width > 0 ? { left: rect.left / k, width: rect.width / k } : null);
    };
    measure();
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    observer?.observe(anchor);
    return () => { window.removeEventListener('resize', measure); observer?.disconnect(); };
  }, [dirty]);

  if (!dirty) return null;

  const parts: string[] = [];
  if (owners.products) parts.push(t('catalog:bar.inProducts', { count: owners.products }));
  if (owners.categories) parts.push(t('catalog:bar.inCategories', { count: owners.categories }));
  const meta = parts.length
    ? t('catalog:bar.metaWhere', { count: changes.length, where: parts.join(t('catalog:bar.and')) })
    : t('catalog:bar.meta', { count: changes.length });
  const hasMoney = changes.some(c => c.money);

  return (
    <>
      <div ref={anchorRef} aria-hidden="true" style={{ height: 1 }} />
      <div
        role="region"
        aria-label={t('catalog:bar.region')}
        data-testid="catalog-save-bar"
        className="cx-savebar"
        style={box ? { left: box.left, width: box.width } : { left: 16, right: 16 }}
      >
        {open && (
          <div className="cx-changes" role="dialog" aria-label={t('catalog:bar.whatChanged')}>
            <div className="cx-chg-h">{t('catalog:bar.whatChanged')}<span className="cx-sp" />{changes.length}</div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {changes.map(c => (
                <li key={c.id} className="cx-chg" data-testid="change-row">
                  <div className="cx-chg-t" title={`${c.title} — ${c.what}`}><b>{c.title}</b> <span>{c.what}</span></div>
                  <div className="cx-chg-v">
                    <s>{c.from}</s>
                    <ArrowRight className="cx-ico" aria-label={t('catalog:bar.to')} />
                    <b>{c.to}</b>
                  </div>
                  <button type="button" className="cx-icon-btn sm" disabled={saving}
                    aria-label={t('catalog:bar.undoOne', { what: `${c.title} · ${c.what}` })} title={t('catalog:bar.undoOneTitle')}
                    onClick={() => onUndo(c)}>
                    <Undo2 className="cx-ico" />
                  </button>
                </li>
              ))}
            </ul>
            {hasMoney && <div className="cx-chg-foot">{t('catalog:bar.moneyNote')}</div>}
          </div>
        )}
        <span className="cx-dot2" aria-hidden="true" />
        <div className="cx-msg">
          <b>{t('catalog:bar.message')}</b>
          <span className="cx-meta">{meta}</span>
        </div>
        <span className="cx-sp" />
        <button type="button" className="cx-lnk" aria-expanded={open} onClick={onToggle}>
          {open ? t('catalog:bar.hideList') : t('catalog:bar.whatChangedQ')}
        </button>
        <button type="button" className="cx-btn undo" onClick={onUndoAll} disabled={saving}>
          <Undo2 className="cx-ico" /> {t('catalog:bar.undo')}
        </button>
        <button type="button" className="cx-btn primary" onClick={onSave} disabled={saving || disabled}>
          {saving ? <Loader2 className="cx-ico animate-spin" /> : <Check className="cx-ico" />}
          {saving ? t('catalog:bar.saving') : t('catalog:bar.save')}
        </button>
      </div>
    </>
  );
};

export default CatalogSaveBar;
